import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";

export default function RegisterModal({ open, onOpenChange, event, onRegistered }) {
  const { user } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState("");
  const [motivation, setMotivation] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  const submit = async () => {
    if (!name.trim()) {
      toast.error("Nama lengkap wajib diisi.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/registrations", {
        event_id: event.id, name, phone, motivation, user_id: user?.id || "",
      });
      setSuccess(data);
      onRegistered?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal mendaftar.");
    } finally {
      setSubmitting(false);
    }
  };

  const close = (v) => {
    onOpenChange(v);
    if (!v) { setSuccess(null); setName(user?.name || ""); setPhone(""); setMotivation(""); }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="register-modal" className="max-w-md rounded-2xl">
        {success ? (
          <div className="text-center py-6 space-y-4" data-testid="register-success">
            <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto" />
            <h3 className="font-heading font-extrabold text-xl text-slate-900">Pendaftaran Berhasil!</h3>
            <p className="text-sm text-slate-500">
              Anda relawan ke-{success.registered} dari {success.capacity} untuk <span className="font-bold">{event.title}</span>.
              Gabung grup koordinasi WhatsApp untuk briefing.
            </p>
            <a
              data-testid="whatsapp-redirect-link"
              href={success.whatsapp_link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-6 py-3 rounded-full transition-colors"
            >
              <MessageCircle className="w-5 h-5" /> Gabung Grup WhatsApp
            </a>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading">Ikuti Aksi: {event.title}</DialogTitle>
              <DialogDescription>
                {event.date} · {event.time} · {event.city}. Isi data untuk konfirmasi kehadiran.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <Input data-testid="register-name-input" placeholder="Nama lengkap" value={name} onChange={(e) => setName(e.target.value)} className="rounded-xl" />
              <Input data-testid="register-phone-input" placeholder="Nomor WhatsApp" value={phone} onChange={(e) => setPhone(e.target.value)} className="rounded-xl" />
              <Textarea data-testid="register-motivation-input" placeholder="Motivasi singkat (opsional)" value={motivation} onChange={(e) => setMotivation(e.target.value)} className="rounded-xl" />
              <Button
                data-testid="register-submit-btn"
                onClick={submit}
                disabled={submitting}
                className="w-full rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
              >
                {submitting ? "Memproses..." : "Konfirmasi Pendaftaran"}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
